import TypeColor from 'color'
import React, { useState } from 'react'
import { FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import Flex from 'typescript-styled-flex'
import { useTranslation } from 'react-i18next'
import { toColor } from '../../../utils/ColorTransformator'
import { Color, ColorsContainer, ExpandButton } from '../../ColorPalette'
import { SquareCheckbox } from '../index'
import Label from '../FormElement/Label'
import DisabledIcon from '../../../assets/disabled.png'
import { PopoveredIcon2 } from '../../PopoveredIcon/PopoveredIcon'
import { useMediaQuery, useTheme } from '@material-ui/core'
import UpgradePremiumBlockScreenModal
  from '../../../containers/UpgradePremiumBlockScreenModal/UpgradePremiumBlockScreenModal'

export default function ColorPalette({
  colors,
  onChange,
  value = '#fff',
  onExpandButtonClick,
  className,
  colorLabel = '',
  enabled = true,
  size = 1,
  withCheckbox,
  alwaysExpanded = false,
  withPopoverIcon,
  disabled = false,
                                       disableExpandButton,
}: IColorPaletteProps & IColorPaletteDispatch) {
  const [expand, toggleExpand] = React.useState(false)
  const { t } = useTranslation()
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isModalOpened, setIsModalOpened] = useState(false)
  const handlePopoverOpen = event => {
    if (isMobile) {
      setIsModalOpened(true)
    } else {
      event.preventDefault()
      event.stopPropagation()
      setPopoverAnchor(event.currentTarget)
    }
  }

  const handlePopoverClose = event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setPopoverAnchor(null)
  }

  function toggle() {
    if (onExpandButtonClick) {
      onExpandButtonClick()
    }
    toggleExpand(!expand)
  }

  function setColor(color: string) {
    return function() {
      toggleExpand(!expand)
      onChange(color)
    }
  }

  const typeValue = toColor(value, true)
    .rgb()
    .toString()

  return (
    <React.Fragment>
      <Flex full={true} style={{ alignItems: 'center', position: 'relative' }}>
        {disabled && withPopoverIcon && !isMobile && (
          <Flex
            onClick={handlePopoverOpen}
            style={{
              position: 'absolute',
              zIndex: 100,
              backgroundColor: 'transparent',
              top: 0,
              left: 0,
              right: 30,
              bottom: 0,
            }}
          />
        )}
        {withCheckbox && (
          <Flex style={{ alignItems: 'center' }}>
            <SquareCheckbox.ReduxField
              disabled={disabled}
              size={size}
              isHorizontal={true}
              fireEventOnClick={true}
              isReverse={true}
              label={t('custom_booking_color')}
              isBoldLabel={true}
              noSpacing={true}
              name={'setCustomColor'}
              onChange={setColor('#2196f3')}
            />
            {withPopoverIcon && (
              <PopoveredIcon2
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
              />
            )}
          </Flex>
        )}

        {colorLabel !== '' && (
          <Flex style={{ gap: '8px', alignItems: 'center' }}>
            <Label size={size} horizontal={true} bold={true}>
              {colorLabel}
            </Label>
            {!withCheckbox && withPopoverIcon && <PopoveredIcon2 />}
          </Flex>
        )}
        <Color
          size={size}
          bordered={value === '#fff'}
          style={{
            height: `${30 * size}px`,
            margin: 0,
            marginLeft: 'auto',
            marginRight: `${15 * size}px`,
            minHeight: `${30 * size}px`,
            minWidth: `${30 * size}px`,
            width: `${30 * size}px`,
            opacity: disabled ? 0.4 : 1,
          }}
          color={value}
          className={`${className}-color-selected`}
        />

        {!alwaysExpanded && (
          <ExpandButton
            size={size}
            onClick={disableExpandButton? handlePopoverOpen: toggle}
            style={{zIndex: 400, marginTop: `${4 * size}px` }}
            className={`${className}-button-expand`}
            disabled={!enabled}
          >
            {expand ? (
              <FiChevronUp size={16 * size} />
            ) : (
              <FiChevronDown size={16 * size} />
            )}
          </ExpandButton>
        )}
      </Flex>

      {(expand || alwaysExpanded) && (
        <ColorsContainer
          onClick={disabled ? handlePopoverOpen: undefined}
          size={size}
          withCheckbox={withCheckbox || colorLabel !== ''}
          disabled={disabled}
          disabledIcon={DisabledIcon}
        >
          {colors.map((color: TypeColor, index) => (
            <Color
              size={size}
              bordered={color.hex() === '#FFFFFF'}
              selected={color.rgb().toString() === typeValue}
              color={color.rgb().toString()}
              key={color.hex()}
              onClick={setColor(color.rgb().toString())}
              className={`${className}-color-${index}`}
              style={{ cursor: 'pointer' }}
            >
              {color.rgb().toString() === typeValue ? <FiCheck /> : null}
            </Color>
          ))}
        </ColorsContainer>
      )}
      {!withCheckbox && (
        <PopoveredIcon2
          withIcon={false}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
        />
      )}
      {isMobile && <UpgradePremiumBlockScreenModal
        open={isModalOpened}
        close={() => setIsModalOpened(false)}
      />}
    </React.Fragment>
  )
}

export interface IColorPaletteProps {
  value: string
  colors: TypeColor[]
  className?: string
  enabled?: boolean
  withCheckbox?: boolean
  withPopoverIcon?: boolean
  colorLabel?: string
  alwaysExpanded?: boolean
  size?: number
  disabled?: boolean
  disableExpandButton?: boolean
}

export interface IColorPaletteDispatch {
  onChange: (color: string) => void
  onExpandButtonClick?: () => void
}
